import { Injectable, ElementRef, OnDestroy, Output, EventEmitter } from "@angular/core";
import { BehaviorSubject, Subscription, fromEvent } from "rxjs";
import { skip } from "rxjs/operators";

export interface Box {
	left?: number;
	right?: number;
	top?: number;
	bottom?: number;
	width?: number;
	height?: number;
	x?: number;
	y?: number;
}

@Injectable()
export class PopoverService implements OnDestroy {
	topGen(): number {
		if (this.absolute) {
			if (this.position === "bottom" ||this.overBoth || ((this.overLeft || this.overRight) && (this.position === "right" || this.position === "left"))) {
				return (this.triggerBox.top + this.triggerBox.height + this.offset + this.pageOffset);
			} else if (this.position === "top") {
				return (this.triggerBox.top - this.popoverBox.height - this.offset + window.pageYOffset);
			} else {
				return this.triggerBox.top + window.pageYOffset;
			}
		}
	}

	leftGen(): number {
		if (this.position === "left") {
			return this.triggerBox.left - this.popoverBox.width - this.offset;
		} else if (this.position === "right") {
			return this.triggerBox.left + this.triggerBox.width + this.offset;
		} else if (this.positionX === "left") {
			return this.triggerBox.left;
		} else if (this.positionX === "right") {
			return (
				this.triggerBox.left +
				this.triggerBox.width -
				this.popoverBox.width
			);
		} else {
			// get the center relative to the trigger & popover
			return (
				this.triggerBox.left +
				this.triggerBox.width / 2 -
				this.popoverBox.width / 2
			);
		}
	}

	overflowLeft(): number {
		if (this.absolute) {
		// check if tip box should be flush to the left or can be flush with trigger box
			if (this.overBoth) {
				const left =
					this.triggerBox.x +
					this.triggerBox.width / 2 -
					this.popoverBox.width / 2;
				return left < this.offset ? this.offset : left;
			} else if (this.overRight) {
				const left =
					this.triggerBox.x +
					this.triggerBox.width -
					this.popoverBox.width;
				return left < this.offset ? this.offset : left;
			} else if (this.overLeft) {
				return this.triggerBox.x;
			}
		}
	}

	get overLeft(): boolean {
		return this.leftGen() < 0;
	}

	get overRight(): boolean {
		return (
			this.leftGen() + this.popoverBox.width + this.offset >
			window.innerWidth
		);
	}

	get overBoth(): boolean {
		// IMPROVE: this could probably be moved into a config file associated with this component
		const maxSpace = 300;
		return (
			(this.overLeft &&
				this.triggerBox.x + this.popoverBox.width >
				window.innerWidth) ||
			(this.overLeft && this.triggerBox.x > maxSpace) ||
			(this.overRight &&
				window.innerWidth -
				(this.triggerBox.x + this.triggerBox.width) >
				maxSpace)
		);
	}

	isOpen = false;
	triggerBox: Box = {};
	popoverBox: Box = {};
	offset = 0;

	active: boolean;
	hover = false;
	clickable = true;
	position: "top" | "right" | "bottom" | "left";
	positionX: "left" | "right" | "center" ;
	triggerRef;
	popoverRef;
	absolute;
	pageOffset;

	$popoverToggle: BehaviorSubject<boolean> = new BehaviorSubject(this.isOpen);
	windowEventListeners: Subscription[] = [];
	@Output() popoverToggle = new EventEmitter;

	private _topGen: number;
	private _leftGen: number;
	private _overRight: boolean;
	private _overLeft: boolean;
	private _overBoth: boolean;
	private _overflowLeft: boolean;

	constructor() { }

	ngOnDestroy(): void {
		if (this.$popoverToggle) {
			this.$popoverToggle.unsubscribe();
		}
		if (this.windowEventListeners.length) {
			this.windowEventListeners.forEach(listener => {
				if (listener) {
					listener.unsubscribe();
				}
			});
		}

		// this.$_Popover_reset();
		this.isOpen = false;
		if (this.popoverRef && this.absolute) {
				document.body.removeChild(this.popoverRef);
		}



	}

	syncProps(propsObj: {
		clickable: boolean;
		active: boolean;
		hover: boolean;
		position: "top" | "right" | "bottom" | "left";
		positionX: "left" | "right" | "center";
		offset: number;
		triggerRef;
		popoverRef;
		absolute;
		pageOffset;
	}) {
		this.clickable = propsObj.clickable;
		this.active = propsObj.active;
		this.hover = propsObj.hover;
		this.position = propsObj.position;
		this.positionX = propsObj.positionX;
		this.offset = propsObj.offset;
		this.triggerRef = propsObj.triggerRef;
		this.popoverRef = propsObj.popoverRef;
		this.absolute = propsObj.absolute;
		this.pageOffset = propsObj.pageOffset
	}

	/* popover interaction & position helper methods */
	$_Popover_hoverToggle(display: boolean): void {
		if (this.hover) {
			setTimeout(() => {
				this.isOpen = display;
				this.$_Popover_reset();
			}, 250);
		}
	}

	$_Popover_reset(): void {
		this.$popoverToggle.next(this.isOpen);
		// this.popoverToggle.emit(this.isOpen);
		// if popover is not open, reset 	bounding box values
		if (!this.isOpen) {

			// this.$_Popover_remove(this.popoverRef)
			this.triggerBox = {};
			this.popoverBox = {};
		}
	}

	$_Popover_remove(popoverRef): void {
		const popoverRefFinal = popoverRef ? popoverRef : this.popoverRef;

		if (document.body.contains(popoverRefFinal)) {
			document.body.removeChild(popoverRef);
		}
	}

	$_Popover_toggle(event: Event, popoverRef?: ElementRef): void {
		const popoverEl = popoverRef
			? popoverRef.nativeElement
			: this.popoverRef;

		if (
			!this.hover &&
			this.clickable &&
			!(
				popoverEl === event.target ||
				(popoverEl && popoverEl.contains(event.target))
			)
		) {
			this.isOpen = !this.isOpen;
			// allow popover to be closed on document click
			if (this.isOpen) {
				["click", "touchend"].forEach(handler => {
					this.windowEventListeners.push(
						fromEvent(window, handler)
							.pipe(skip(1))
							.subscribe(
								this.$_Popover_toggleClose() as EventListener
							)
					);
				});
			}

			// if (!this.isOpen) {
			// 	if (popoverEl) {
			// 		setTimeout(() => {
			// 			document.body.removeChild(popoverEl);
			// 		}, 0);
			// 	}
			// }
			this.$_Popover_reset();
		}
	}

	$_Popover_toggleClose(): (event: Event) => void {
		const instance = this;

		return (event: Event) => {
			const popoverEl = instance.popoverRef ? instance.popoverRef : null;
			if (
				popoverEl &&
				!(
					popoverEl === event.target ||
					popoverEl.contains(event.target as Node)
				) &&
				!(
					popoverEl === event.target ||
					popoverEl.contains(event.target as Node)
				)
			) {
				instance.isOpen = false;
				instance.$_Popover_reset();
				instance.windowEventListeners.forEach(listener => {
					if (listener) {
						listener.unsubscribe();
					}
				});
			}
		};
	}

	/*
		Calculates the position of the popover box and the trigger box
	*/
	$_Popover_position(popoverEl: HTMLElement) {
		// ensure popover is unaffected by styles of parent elements by moving it to the body
		document.body.appendChild(popoverEl);
		// setTimeout(() => document.getElementById(popoverEl.id).focus(), 0);
		if (popoverEl) {
			// REVIEW: positioning absolutely to the body has been removed because transitions cannot be applied to a popover
			// that exists out of the template, unless the transition component can be made dynamically with it, which in the
			// current state of angular is quite messy.
			if (this.absolute) {
				setTimeout(() => {
					popoverEl.focus();
				},25);
			}
			// grab bounding box of trigger and popover

			this.triggerBox = (this.triggerRef as Element).getBoundingClientRect();
			this.popoverBox = popoverEl.getBoundingClientRect();
		}
	}

	// attach this to your animation hook, to initialize the bounding box of the trigger and the popover
	enter(popoverEl) {
		if (this.absolute) {
			this.$_Popover_position(popoverEl);
		}
	}
}
